import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { 
  Plus, 
  Loader2,
  Search,
  Stethoscope,
  Calendar,
  Clock,
  ChevronRight,
  Filter,
  Play,
  CheckCircle,
  XCircle,
  Video,
  Phone
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const consultationTypeIcons = {
  physical: Stethoscope,
  video: Video,
  phone: Phone,
  follow_up: Calendar,
};

const consultationTypeLabels = {
  physical: 'Physical',
  video: 'Video Call',
  phone: 'Phone',
  follow_up: 'Follow-up',
};

const statusColors = {
  scheduled: 'bg-info/10 text-info border-info/20',
  in_progress: 'bg-warning/10 text-warning border-warning/20',
  completed: 'bg-success/10 text-success border-success/20',
  cancelled: 'bg-destructive/10 text-destructive border-destructive/20',
};

const statusLabels = {
  scheduled: 'Scheduled',
  in_progress: 'In Progress',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

function formatDate(dateStr) {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-IN', { 
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'Asia/Kolkata'
  });
}

function formatDateTime(dateStr) {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-IN', { 
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Asia/Kolkata'
  });
}

export default function ConsultationsListPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [consultations, setConsultations] = useState([]);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  
  // Create modal
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    patient_id: '',
    consultation_date: new Date().toISOString().split('T')[0],
    consultation_type: 'physical',
    notes: '',
  });

  const fetchConsultations = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (typeFilter !== 'all') params.append('consultation_type', typeFilter);
      
      const { data } = await axios.get(`${API_URL}/api/consultations?${params}`, { withCredentials: true });
      setConsultations(data);
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to fetch consultations');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, typeFilter]);

  const fetchPatients = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/patients?is_active=true`, { withCredentials: true });
      setPatients(data);
    } catch (error) {
      console.error('Failed to fetch patients', error);
    }
  }, []);

  useEffect(() => {
    fetchConsultations();
    fetchPatients();
  }, [fetchConsultations, fetchPatients]);

  const resetForm = () => {
    setFormData({
      patient_id: '',
      consultation_date: new Date().toISOString().split('T')[0],
      consultation_type: 'physical',
      notes: '',
    });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/consultations`, formData, { withCredentials: true });
      toast.success('Consultation created successfully');
      setIsCreateOpen(false);
      resetForm();
      navigate(`/consultations/${data.id}`);
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to create consultation');
    } finally {
      setIsSubmitting(false);
    }
  };

  const stats = {
    total: consultations.length,
    scheduled: consultations.filter(c => c.status === 'scheduled').length,
    inProgress: consultations.filter(c => c.status === 'in_progress').length,
    completed: consultations.filter(c => c.status === 'completed').length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="consultations-loading">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="consultations-list-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-medium tracking-tight text-foreground">
            Consultations
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage patient consultations and appointments
          </p>
        </div>
        <Button 
          onClick={() => { resetForm(); setIsCreateOpen(true); }}
          className="bg-primary hover:bg-primary/90 rounded-full"
          data-testid="create-consultation-button"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Consultation
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-border shadow-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <Stethoscope className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-2xl font-heading font-semibold">{stats.total}</p>
              <p className="text-xs text-muted-foreground">Total</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border shadow-sm cursor-pointer hover:shadow-md transition-shadow" onClick={() => setStatusFilter('scheduled')}>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-info/10">
              <Clock className="w-5 h-5 text-info" />
            </div>
            <div>
              <p className="text-2xl font-heading font-semibold">{stats.scheduled}</p>
              <p className="text-xs text-muted-foreground">Scheduled</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border shadow-sm cursor-pointer hover:shadow-md transition-shadow" onClick={() => setStatusFilter('in_progress')}>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-warning/10">
              <Play className="w-5 h-5 text-warning" />
            </div>
            <div>
              <p className="text-2xl font-heading font-semibold">{stats.inProgress}</p>
              <p className="text-xs text-muted-foreground">In Progress</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border shadow-sm cursor-pointer hover:shadow-md transition-shadow" onClick={() => setStatusFilter('completed')}>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-success/10">
              <CheckCircle className="w-5 h-5 text-success" />
            </div>
            <div>
              <p className="text-2xl font-heading font-semibold">{stats.completed}</p>
              <p className="text-xs text-muted-foreground">Completed</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="border-border shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-40 rounded-xl" data-testid="status-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="scheduled">Scheduled</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-full sm:w-40 rounded-xl" data-testid="type-filter">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="physical">Physical</SelectItem>
                <SelectItem value="video">Video Call</SelectItem>
                <SelectItem value="phone">Phone</SelectItem>
                <SelectItem value="follow_up">Follow-up</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Consultations List */}
      <div className="space-y-4">
        {consultations.length === 0 ? (
          <Card className="border-border shadow-sm">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Stethoscope className="w-12 h-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground text-center">
                No consultations found
              </p>
            </CardContent>
          </Card>
        ) : (
          consultations.map((consultation) => {
            const TypeIcon = consultationTypeIcons[consultation.consultation_type] || Stethoscope;
            return (
              <Card 
                key={consultation.id} 
                className="border-border shadow-sm card-interactive cursor-pointer"
                onClick={() => navigate(`/consultations/${consultation.id}`)}
                data-testid={`consultation-card-${consultation.id}`}
              >
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                        <TypeIcon className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-heading font-medium">{consultation.patient_name}</h3>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Calendar className="w-4 h-4" />
                          <span>{formatDate(consultation.consultation_date)}</span>
                          <span>•</span>
                          <span>{consultationTypeLabels[consultation.consultation_type]}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge variant="outline" className={statusColors[consultation.status]}>
                        {statusLabels[consultation.status]}
                      </Badge>
                      {consultation.transcript && (
                        <Badge variant="secondary" className="text-xs">Has Transcript</Badge>
                      )}
                      {consultation.ai_summary && (
                        <Badge variant="secondary" className="text-xs">AI Summary</Badge>
                      )}
                      <ChevronRight className="w-5 h-5 text-muted-foreground" />
                    </div>
                  </div>
                  {consultation.notes && (
                    <p className="text-sm text-muted-foreground mt-3 pl-16 line-clamp-2">
                      {consultation.notes}
                    </p>
                  )}
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Create Consultation Modal */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="create-consultation-modal">
          <DialogHeader>
            <DialogTitle className="font-heading">New Consultation</DialogTitle>
            <DialogDescription>Schedule a new patient consultation</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="create-patient">Patient *</Label>
              <Select value={formData.patient_id} onValueChange={(value) => setFormData({ ...formData, patient_id: value })}>
                <SelectTrigger className="rounded-xl" data-testid="create-consultation-patient">
                  <SelectValue placeholder="Select a patient" />
                </SelectTrigger>
                <SelectContent>
                  {patients.map((patient) => (
                    <SelectItem key={patient.id} value={patient.id}>
                      {patient.name} ({patient.phone})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="create-date">Date *</Label>
                <Input
                  id="create-date"
                  type="date"
                  value={formData.consultation_date}
                  onChange={(e) => setFormData({ ...formData, consultation_date: e.target.value })}
                  required
                  className="rounded-xl"
                  data-testid="create-consultation-date"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="create-type">Type *</Label>
                <Select value={formData.consultation_type} onValueChange={(value) => setFormData({ ...formData, consultation_type: value })}>
                  <SelectTrigger className="rounded-xl" data-testid="create-consultation-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="physical">Physical</SelectItem>
                    <SelectItem value="video">Video Call</SelectItem>
                    <SelectItem value="phone">Phone</SelectItem>
                    <SelectItem value="follow_up">Follow-up</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="create-notes">Initial Notes</Label>
              <Input
                id="create-notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Optional notes..."
                className="rounded-xl"
                data-testid="create-consultation-notes"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsCreateOpen(false)} className="rounded-full">
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={isSubmitting || !formData.patient_id} 
                className="bg-primary hover:bg-primary/90 rounded-full" 
                data-testid="create-consultation-submit"
              >
                {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Create Consultation
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
